import { useQuery } from '@tanstack/react-query';
import { apiUrlGenerator } from 'infrastructure/hooks/use-api';
import dataService from 'services/dataService';
import { useEffect } from 'react';
import { showResult } from 'infrastructure/hooks/utils';
import { useUpdateAssigneeOverviewDashboard } from 'infrastructure/hooks/assign-patient';
import { PATIENTS_PER_PAGE } from 'components/organisms/rpm-overview-dashboard';

import { buildRpmOverviewDashboardFilter } from './utils';

import type { Nullable } from 'infrastructure/types';
import type { TOverviewDashboardFilter } from './use-filter';
import type {
  ClinicDoNotCallRestrictionType,
  DoNotCallRestrictionType,
  Languages,
  OverviewDashboardSortEnum,
  ReadingStatus,
  Timezones,
} from 'infrastructure/enums';
import type {
  IAssignee,
  IPartialPaginationMeta,
} from 'infrastructure/interfaces';
import type { RpmPatientLatestReading } from './use-patient-latest-reading';

export interface RpmOverviewPatient {
  name: string;
  guid: string;
  dob: string;
  language: Languages;
  provider: string;
  readings: {
    normal: number;
    atRisk: number;
    critical: number;
  };
  assignee?: IAssignee;
  overTime?: boolean;
  pmpmStatus?: boolean | null;
  interactionStatus?: boolean;
  latestReading?: RpmPatientLatestReading | null;
  doNotCallRestrictionType?: Nullable<DoNotCallRestrictionType>;
  doNotCallStartTime?: Nullable<string>;
  doNotCallEndTime?: Nullable<string>;
  clinicDoNotCallRestrictionType?: Nullable<ClinicDoNotCallRestrictionType>;
  clinicDoNotCallStartTime?: Nullable<string>;
  clinicDoNotCallEndTime?: Nullable<string>;
  timezone?: Nullable<string>;
  // Pod fields — optional; existing non-pod patients will have these as undefined
  podSection?: 'primary' | 'secondary' | 'tertiary' | 'outside' | null;
  podName?: string | null;
  clinicGuid?: string | null;
}

export interface RpmOverviewDashboardResponse {
  data: RpmOverviewPatient[];
  count: number;
}

export type TDataFilterWithPaginationMeta = TOverviewDashboardFilter &
  IPartialPaginationMeta;

export type TDataFilter = Omit<
  TDataFilterWithPaginationMeta,
  'doctorGuid' | 'startTime' | 'endTime' | 'status' | 'timezone' | 'sorting'
> & {
  childClinic?: boolean;
  latestReadingStartTime?: number;
  latestReadingEndTime?: number;
  year?: number;
  month?: number;
  readingStatus?: ReadingStatus;
  latestReading: OverviewDashboardSortEnum;
  timezone?: Timezones[];
  // Required for SLA patient exclusion — without this, getSlaPatientGuids has no agency scope
  // and SLA patients are not excluded from the overview (appear in both sections simultaneously).
  agencyGuid?: string[];
};

const url = 'reports/reports/interactive-time-dashboard-overview';

export const getRpmOverviewDashboardQueryKey = (filter: TDataFilter) => [
  url,
  filter,
];

const queryFn = async (filter: TDataFilter) => {
  const { data, error } =
    await dataService.getList<RpmOverviewDashboardResponse>(
      apiUrlGenerator(url, filter),
    );

  if (error) {
    return Promise.reject(error);
  }

  return data;
};

export const useRpmOverviewDashboard = (
  filter: TDataFilterWithPaginationMeta,
  // agencyGuid must be passed as multi-value query params so the backend can build the SLA
  // exclusion list (getSlaPatientGuids). Without it, SLA patients appear in both sections.
  agencyGuid?: string[],
) => {
  const filterWithItems = buildRpmOverviewDashboardFilter(filter, {
    withPagination: true,
    itemsPerPage: PATIENTS_PER_PAGE,
  });

  const filterWithAgency: TDataFilter = { ...filterWithItems, agencyGuid };
  const queryKey = getRpmOverviewDashboardQueryKey(filterWithAgency);

  const { data, isLoading, isFetching, error } = useQuery<
    RpmOverviewDashboardResponse | undefined,
    string | undefined
  >({
    queryKey,
    queryFn: async () => queryFn(filterWithAgency),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (error) {
      showResult(error);
    }
  }, [error]);

  useUpdateAssigneeOverviewDashboard(queryKey);

  return {
    patients: data,
    isLoading,
    isFetching,
  };
};
